import {DocumentNode, OperationVariables} from '@apollo/client';
import type {JSX} from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {AccessibleGraphQLObject, accessibleObjs} from 'graphql-client/InaccessibleInfo';

import {InaccessibleInfoGraphQL} from '../../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {CardSectionRowProps, renderQueryLoadingMessage, renderQueryLoadingOrError} from '../CardSection';

interface UseMyWorkRowsOptions<
  TData,
  TVariables extends OperationVariables,
  TItem extends AccessibleGraphQLObject & {modifiedAt: string},
> {
  query: DocumentNode;
  variables: TVariables;
  /** True while a parent summary query has not resolved yet (row IDs are still null). */
  skip: boolean;
  getItems: (data: TData) => Array<TItem | InaccessibleInfoGraphQL>;
  toRowProps: (item: TItem) => CardSectionRowProps;
  messages: {loading: string; error: string};
  testComponent: string;
}

type UseMyWorkRowsResult =
  | {type: QueryResultType.SUCCESS; rows: Array<CardSectionRowProps>}
  | {type: Exclude<QueryResultType, QueryResultType.SUCCESS>; element: JSX.Element};

export function useMyWorkRows<
  TData,
  TVariables extends OperationVariables,
  TItem extends AccessibleGraphQLObject & {modifiedAt: string},
>(options: UseMyWorkRowsOptions<TData, TVariables, TItem>): UseMyWorkRowsResult {
  const queryResult = useSkippableBenchlingQuery<TData, TVariables>(options.query, {
    variables: options.variables,
    skip: options.skip,
  });

  if (queryResult.type === QueryResultType.SUCCESS) {
    const accessibleItems = accessibleObjs<TItem>(options.getItems(queryResult.data));
    const rows = accessibleItems
      .sort((a, b) => b.modifiedAt.localeCompare(a.modifiedAt))
      .map((item) => options.toRowProps(item));

    return {type: QueryResultType.SUCCESS, rows};
  }

  // When skip is set, Apollo reports SKIPPED; show a loading row until the summary pre-query yields IDs.
  const element = options.skip
    ? renderQueryLoadingMessage(options.messages.loading, options.testComponent)
    : renderQueryLoadingOrError(queryResult, options.messages, options.testComponent)!;

  return {type: queryResult.type, element};
}
